/**
 * What saving an edited pricing entry does to the pack library.
 *
 * Editing a price composes in the same Bundle Builder as adding (ADR-0060), so
 * the dialog has to decide, per save, which of four things happens to the
 * library. The decision is pure and consequential — it mints rows and re-points
 * entries — so it lives here rather than inline in the dialog, where it could
 * only be checked by driving a browser.
 *
 * The rules are spec 13's, not this file's invention:
 *
 *  - **R3** — a pack referenced by any listing entry, order line or quote line
 *    is immutable; "editing" it creates a new version, and versioning re-points
 *    nothing by itself, so the caller must move the entry to the returned id.
 *    An unreferenced pack edits in place.
 *  - **R3, again** — a family is fixed to the scale it was created on, so
 *    `storePacks.version` cannot re-scale. A composition over a different label
 *    set therefore *cannot* be a new version of the same pack.
 *  - **R2** — "a run that genuinely omits a size is a different size curve and
 *    belongs on its own scale", which is what makes minting a new pack the
 *    correct answer in that case rather than a workaround.
 *
 * Note in practice `referenced` is nearly always true on a saved product:
 * `isPackReferenced` counts a jsonb hit on any listing's `pricing_entries`, so
 * a saved entry makes its own pack referenced. `"edit"` fires mainly for a pack
 * minted earlier in the same unsaved form session.
 */

/** The four outcomes, in the order the dialog checks them. */
export type PackSaveAction =
  /** Bundle unchanged — no library write at all; keep the entry's own `packId`. */
  | { kind: "reuse" }
  /** Same scale, referenced pack — `storePacks.version`; entry re-points. */
  | { kind: "version" }
  /** Same scale, unreferenced pack — `storePacks.edit`, in place. */
  | { kind: "edit" }
  /**
   * `ensureScale` + `storePacks.create`. `reason` is why, and the dialog says
   * which before the click:
   *  - `different-curve` — the size set changed, so R2/R3 forbid a version;
   *  - `pack-unavailable` — the entry's pack can't be resolved (archived, or
   *    superseded by a newer version of its family, which `storePacks.list`
   *    filters out). It can be neither versioned nor edited — the server
   *    refuses a non-current pack with `PackNotCurrentError` — so a changed
   *    composition has nowhere to go but a new pack.
   */
  | { kind: "create"; reason: "different-curve" | "pack-unavailable" };

export interface PackSaveActionInput {
  /** The entry as it stands: its serialised composition and its pack's name. */
  entrySizeRatio: string;
  entryLabel: string;
  /** The bundle currently in the builder. */
  nextSizeRatio: string;
  nextName: string;
  nextScaleLabels: readonly string[];
  /**
   * The live pack's ordered scale labels, or `null` when it can't be resolved —
   * archived out of the active list, or the library query hasn't landed. A pack
   * we can't see is one we must not mutate.
   */
  packScaleLabels: readonly string[] | null;
  /** Whether the live pack is cited by any entry, order line or quote line. */
  referenced: boolean;
}

/** Same labels, order-insensitive — a scale is a set here, not a sequence. */
function sameLabelSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const inB = new Set(b);
  return a.every((label) => inB.has(label));
}

/**
 * Decide what a save does to the library. Pure: no clock, no IO, no state.
 *
 * `"reuse"` covers the most common edit by far — fixing a price — and is what
 * keeps that from versioning a pack into a row identical to its parent.
 */
export function packSaveAction(input: PackSaveActionInput): PackSaveAction {
  const unchanged =
    input.nextSizeRatio === input.entrySizeRatio && input.nextName === input.entryLabel;
  // Nothing moved — money and colour save against the entry as it stands, and
  // an unresolvable pack is irrelevant because we aren't touching it.
  if (unchanged) return { kind: "reuse" };

  // Changed, but the pack can't be resolved. It must NOT be "reuse": the caller
  // would emit the new composition while keeping the old `packId`, leaving the
  // entry claiming a composition its pack does not have. Mint instead.
  if (input.packScaleLabels === null) {
    return { kind: "create", reason: "pack-unavailable" };
  }

  if (sameLabelSet(input.packScaleLabels, input.nextScaleLabels)) {
    return input.referenced ? { kind: "version" } : { kind: "edit" };
  }
  return { kind: "create", reason: "different-curve" };
}
